import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Chip } from "../../../../assets/components/Chip";
import PlaceholderB from "../../../../assets/components/PlaceholderB";

const THEME_OPTIONS = ["매장 홍보", "상품 소개", "이벤트"];

// ── helpers ─────────────────────────────────────────────
const norm = (s = "") => String(s).trim().replace(/\s+/g, "").toLowerCase();
const sanitize = (x) => {
  const s = String(x ?? "").trim();
  return s === "" || s === "null" || s === "undefined" ? "" : s;
};
// "1|2|3" 또는 라벨 → 정확 라벨
function mapAiToTheme(val) {
  if (val == null) return null;
  const s = String(val).trim();
  const byCode = { "1": "매장 홍보", "2": "상품 소개", "3": "이벤트" };
  if (byCode[s]) return byCode[s];
  const hit = THEME_OPTIONS.find((t) => norm(t) === norm(s));
  return hit || null;
}

const PLACEHOLDER_BY_THEME = {
  [norm("매장 홍보")]: "예) 합정역에서 도보 3분, 바로 픽업!",
  [norm("상품 소개")]: "예) 마다가스카르 바닐라빈, 향부터 다른 시그니처 바닐라 라떼",
  [norm("이벤트")]: "예) 11시 전 주문 시 아메리카노 2,000원 행사 진행중!",
};


// 안내문
function GuidanceByTheme({ theme, rightNode }) {
  const COPY = {
    "매장 홍보": {
      title: "매장 홍보 내용",
      required: false,
      hint: "위치/편의/특징을 작성해 주세요.",
    },
    "상품 소개": {
      title: "상품 소개 내용",
      required: true,
      hint: "소개할 상품에 대한 내용을 작성해 주세요.",
    },
    이벤트: {
      title: "이벤트 내용",
      required: false,
      hint: "진행 중인 이벤트를 작성해 주세요.",
    },
    default: {
      title: "광고 내용",
      required: false,
      hint: "위치/편의/특징을 작성해 주세요.",
    },
  };
  const item = COPY[theme] ?? COPY.default;
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between gap-[8px]">
        <p className="text-t4 text-text-default flex-1">
          {item.title}
          <span className={`${item.required ? "text-text-danger" : "text-text-disabled"}`}>
            {item.required ? "(필수)" : "(선택)"}
          </span>
        </p>
        {rightNode && <div className="shrink-0">{rightNode}</div>}
      </div>
      <p className="text-b1 text-text-subtler">{item.hint}</p>
    </div>
  );
}

/**
 * props
 * - isOpen, onClose
 * - value, detailValue
 * - aiTitle, aiReco
 * - data, exData, updateData
 * - onConfirm({ theme, customText })
 * - height: BottomSheet 높이 (예: "h-[80%]")
 */
export default function TitleSelectSheet({
  isOpen,
  onClose,
  value,
  detailValue,
  aiTitle,
  aiReco,
  data,
  exData,
  updateData,
  onConfirm,
  height = "h-[70%]",
}) {
  // 🔹 AgeSelectSheet처럼: AI 추천값 계산 (props 기반)
  const recommendedTheme = useMemo(
    () => mapAiToTheme(sanitize(aiTitle)) || mapAiToTheme(sanitize(aiReco)),
    [aiTitle, aiReco]
  );

  // 시트 내부에서만 쓰는 선택값 / 텍스트
  const [selectedTheme, setSelectedTheme] = useState("");
  const [customText, setCustomText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const raw = sanitize(value);
    const explicit = mapAiToTheme(raw) || raw; // 숫자/라벨 모두 허용
    const exists =
      !!explicit && THEME_OPTIONS.some((t) => norm(t) === norm(explicit));

    const initial = exists
      ? THEME_OPTIONS.find((t) => norm(t) === norm(explicit)) || ""
      : recommendedTheme || "";

    setSelectedTheme(initial);
    // setCustomText(detailValue ?? "");
    const hasDetail =
      typeof detailValue === "string" && detailValue.trim() !== "";

    const initialDetail = 
      hasDetail ? detailValue : exData?.ad_text_override ?? exData?.ad_text ?? "";
    setCustomText(initialDetail);
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isValid =
    !!selectedTheme &&
    (selectedTheme !== "상품 소개" || (customText && customText.trim() !== ""));

  const handleSave = () => {
    if (!isValid) return;
    updateData?.({
      theme: selectedTheme,
      customText: customText,
      ad_text_override: customText,
    });
    onConfirm?.({ theme: selectedTheme, customText });
    onClose?.();
  };

  // AI 자동 생성 (data.theme 기반 + updateData로 반영)
  const genMarketingCopy = async () => {
    const themeToUse = selectedTheme || data?.theme;
    if (!themeToUse) {
      alert("주제를 선택해 주세요.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/camera/gen/copy`,
        {
          store_name: localStorage.getItem("storeName") || "",
          main: exData?.main,
          road_name: localStorage.getItem("roadName") || "",
          category: String(data?.category || data?.customMenu || "").trim(),
          temp: exData?.temp,
          theme: themeToUse,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const text = response?.data?.copyright ?? "";
      setCustomText(text);
      updateData?.({ customText: text });
    } catch (error) {
      console.error("자동화 광고 생성 중 오류:", error);
      alert("광고 생성에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const currentPlaceholder =
    PLACEHOLDER_BY_THEME[norm(selectedTheme)] ||
    "예) 합정역에서 도보 3분, 바로 픽업!";

  return (
    <BottomWithButton
      isOpen={isOpen}
      onClose={onClose}
      title="맞춤 주제"
      subtitle="무엇을 홍보하고 싶나요?"
      height={height}
      onSubmit={handleSave}
      buttonLabel="저장"
      buttonDisabled={!isValid}
      // 하단 버튼을 SplitRowQuarter로 커스터마이즈
      renderButton={({ onClick, disabled }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={onClose}
          rightType="fill"
          rightText="저장"
          onRight={onClick}
          rightDisabled={disabled}
        />
      )}
    >
      <div className="flex flex-col">
        {/* 칩 선택 (AgeSelectSheet 스타일) */}
        <div className="pb-[24px] flex overflow-x-auto gap-[8px] no-scrollbar cursor-grab active:cursor-grabbing">
          {THEME_OPTIONS.map((th) => {
            const isSelected = norm(selectedTheme) === norm(th);
            const isReco =
              recommendedTheme && norm(recommendedTheme) === norm(th);

            return (
              <div key={th} className="flex-shrink-0">
                <Chip
                  chip="a"
                  status={isSelected ? "selected" : "default"}
                  label={th}
                  badgeLabel={isReco ? "AI 추천" : undefined}
                  allowBadge
                  onClick={() => {
                    // theme 변경
                    setSelectedTheme(th);
                    // customText 초기화
                    setCustomText("");
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* 안내 + 입력 + 작은 'AI 자동 생성' 버튼 */}
        <div className="flex flex-col gap-[16px]">          
          <GuidanceByTheme
            theme={selectedTheme || "default"}
            rightNode={              
              <Chip
                chip="f"
                label="AI 자동 생성"
                onClick={isLoading ? undefined : genMarketingCopy}
              />
            }
          />

          <PlaceholderB
            placeholder={currentPlaceholder}
            showCounter
            size="lg"
            maxLength={100}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
          />
        </div>
      </div>
    </BottomWithButton>
  );
}
